import { useFormik } from 'formik';
import React from 'react';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import * as Yup from 'yup';

import { postLogin } from '../api/POST_login';
import Alert from '../components/Alert';
import ClearIndicator from '../components/ClearIndicator';
import DropdownIndicator from '../components/DropdownIndicator';
import { AuthContext } from '../context/authContext';
import { customStyles } from '../utils/customStyles';
import { _storeLocalStorageItem } from '../utils/localStorage';
import { level } from '../utils/staticData';
import { AccessLevel, LoginInterface } from './Login.type';

const Login = () => {
	const { login } = React.useContext(AuthContext);
	const history = useHistory();

	const validationSchema = Yup.object().shape({
		email: Yup.string()
			.email('Format email yang kamu masukkan salah')
			.required('Email tidak boleh kosong'),
		password: Yup.string().required('Password tidak boleh kosong'),
		selectedLevel: Yup.object().shape({
			label: Yup.string().required(),
			value: Yup.string().required(),
		}),
	});
	const {
		values,
		handleBlur,
		handleChange,
		handleSubmit,
		setFieldValue,
		touched,
		errors,
		resetForm,
	} = useFormik<LoginInterface>({
		initialValues: {
			email: '',
			password: '',
			selectedLevel: null,
			isAlertVisible: false,
			isLoading: false,
			errorMessage: '',
		},
		validateOnMount: true,
		validationSchema,
		onSubmit: async (formValues) => {
			await postLogin({
				username: formValues.email,
				password: formValues.password,
				accessLevel: formValues.selectedLevel.value,
			}).then(async ({ data, messageData, messageDescription, status }) => {
				if (data) {
					const { accessToken, accessLevel, name, email, role } = data;
					const profile = {
						name,
						email,
						role,
					};

					const profileStringify = JSON.stringify(profile);

					if (status === 'success') {
						resetForm({
							values: {
								email: '',
								password: '',
								selectedLevel: null,
								isAlertVisible: false,
								isLoading: false,
								errorMessage: '',
							},
						});

						login({
							accessToken,
						});

						_storeLocalStorageItem({
							storageKey: 'accessLevel',
							storageValue: accessLevel,
						});
						_storeLocalStorageItem({
							storageKey: 'profile',
							storageValue: profileStringify,
						});

						if (accessLevel === 'InventoryControl') {
							history.replace('/inventory-control');
						} else {
							history.replace('/tech-support');
						}
					}
				} else {
					setFieldValue('isAlertVisible', true);
					let errMessage = `${messageDescription}. ${messageData}`;
					if (!(messageDescription || messageData)) {
						errMessage =
							'Terjadi kesalahan, mohon ulangi kembali beberapa saat';
					}
					setFieldValue('errorMessage', errMessage);
					toggleLoading();
				}
			});
		},
	});

	const isValid = Object.keys(errors).length > 0;

	const onChangeLevel = (selectedLevel: AccessLevel) => {
		setFieldValue('selectedLevel', selectedLevel);
	};

	const toggleLoading = () => setFieldValue('isLoading', !values.isLoading);

	const onSubmit = () => {
		toggleLoading();
		handleSubmit();
	};

	return (
		<div className="h-screen flex justify-center items-center bg-gray-gray200">
			<div className="w-full max-w-md2">
				<div className="font-interMedium text-extraLarge2 text-gray-gray700 mb-10 text-center">
					Masuk ke Sistem Stocktake
				</div>
				<Alert showAlert={values.isAlertVisible} text={values.errorMessage} />
				<div className="bg-white rounded-xl shadow-default p-6 mb-6">
					<div className="font-interMedium text-extraLarge text-gray-gray700 mb-6">
						Informasi Login
					</div>
					<form>
						<div>
							<div className="mb-3">
								<label className="text-medium font-interSemiBold text-gray-gray600 uppercase">
									Email
								</label>
							</div>
							<input
								autoFocus
								type="email"
								onChange={handleChange('email')}
								onBlur={handleBlur('email')}
								value={values.email}
								className={
									touched.email && errors.email
										? 'w-full p-4 text-large font-interRegular text-gray-gray700 border-2 border-danger-default rounded-xl outline-none transition duration-150 ease-in-out'
										: 'w-full p-4 text-large font-interRegular text-gray-gray700 border border-gray-gray500 focus:border-2 focus:border-gray-gray700 rounded-xl outline-none transition duration-150 ease-in-out mb-4'
								}
								placeholder="Masukkan email"
							/>
							{touched.email && errors.email && (
								<div className="text-medium font-interRegular text-danger-default my-4">
									{errors.email}
								</div>
							)}
						</div>
						<div>
							<div className="mb-3">
								<label className="text-medium font-interSemiBold text-gray-gray600 uppercase">
									Password
								</label>
							</div>
							<input
								type="password"
								onChange={handleChange('password')}
								onBlur={handleBlur('password')}
								value={values.password}
								className={
									touched.password && errors.password
										? 'w-full p-4 text-large font-interRegular text-gray-gray700 border-2 border-danger-default rounded-xl outline-none transition duration-150 ease-in-out'
										: 'w-full p-4 text-large font-interRegular text-gray-gray700 border border-gray-gray500 focus:border-2 focus:border-gray-gray700 rounded-xl outline-none transition duration-150 ease-in-out mb-4'
								}
								placeholder="Masukkan password"
							/>
							{touched.password && errors.password && (
								<div className="text-medium font-interRegular text-danger-default my-4">
									{errors.password}
								</div>
							)}
						</div>
						<div>
							<div className="mb-3">
								<label className="text-medium font-interSemiBold text-gray-gray600 uppercase">
									Jenis Akses
								</label>
							</div>
							<Select
								styles={customStyles}
								components={{
									ClearIndicator,
									DropdownIndicator,
									IndicatorSeparator: null,
								}}
								isClearable
								name="accessLevel"
								options={level}
								noOp
								placeholder={
									values.selectedLevel
										? values.selectedLevel.label
										: 'Pilih Jenis Akses'
								}
								value={values.selectedLevel}
								onChange={onChangeLevel}
								noOptionsMessage={() => 'Pencarian tidak ditemukan'}
							/>
						</div>
					</form>
				</div>
				<button
					disabled={isValid || values.isLoading}
					onClick={onSubmit}
					type="submit"
					className={
						isValid
							? 'w-full bg-gray-gray300 rounded-xl flex items-center justify-center p-4'
							: 'w-full bg-primary-green rounded-xl flex items-center justify-center p-4'
					}
				>
					{values.isLoading ? (
						<div>
							<img
								src="/images/spinner.gif"
								alt="spinner"
								className="w-6 h-6"
							/>
						</div>
					) : (
						<div
							className={
								isValid
									? 'text-gray-gray500 text-large font-interSemiBold'
									: 'text-white text-large font-interSemiBold'
							}
						>
							Masuk
						</div>
					)}
				</button>
			</div>
		</div>
	);
};

export default Login;
